import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import {
  FormContext, Article, Desc, HelpBlock, ReflowContainer, Title, Input, InputContainer, Button,
} from '../form-context';
import { useForm } from '../../../../shared/form-validator-hook';
import Loading from '../../../../shared/loading';
import { usePostSignIn, useGetMyProfile } from '../../../../services/auth-service';
import oauthProviderDisplayName from '../../../shared/sign-up/oauth-provider-label';
import { changeBrazeUserIfEnabled } from '../../../../services/braze/braze-user-identity';

function getSignInError(res, err) {
  if (_.get(res, 'status') !== 201 && _.get(err, 'response.status') === 401) {
    const errorData = _.get(err, 'response.data', {});
    return {
      message: errorData.error || null,
      provider: errorData.provider || null,
    };
  }
  return null;
}

function renderSignInError(signInError) {
  if (signInError.provider) {
    return (
      <>
        {'You have an "Email Only" account using'}
        {' '}
        {oauthProviderDisplayName(signInError.provider)}
        {' '}
        {'oAuth. Sign in using'}
        {' '}
        {oauthProviderDisplayName(signInError.provider)}
        {' '}
        {'oAuth to access your account.'}
      </>
    );
  }
  if (signInError.message?.includes('complete registration')) {
    return (
      <>
        {'You have an "Email Only" account and need to'}
        {' '}
        <a href="/sign_up">complete registration</a>
        {' '}
        {'before you can Sign In'}
      </>
    );
  }
  return signInError.message || signInError;
}

export default function SignInForm({ ebookName }) {
  const { dispatch } = React.useContext(FormContext);
  const {
    values, useInput, isValid, errors,
  } = useForm({
    email: '',
    password: '',
  }, { 'data-error': 'show' });
  const [{ response, loading: signInLoading, error }, signIn] = usePostSignIn();
  const [{ data: profile, loading: profileLoading }, getProfile] = useGetMyProfile();
  const [signInError, setSignInError] = React.useState(null);
  const loadingRef = React.useRef();

  function switchToSignUp(e) {
    e.preventDefault();
    dispatch({ type: 'stepToSignUp' });
  }

  React.useEffect(() => {
    loadingRef.current && loadingRef.current.toggle(signInLoading || profileLoading);
  }, [signInLoading, profileLoading]);

  React.useEffect(() => {
    const err = getSignInError(response, error);
    if (response && !err) {
      const brazeExternalId = _.get(response, 'data.braze_external_id');
      if (brazeExternalId) {
        changeBrazeUserIfEnabled(brazeExternalId);
      }
      getProfile();
    } else if (err) {
      setSignInError(err);
    }
  }, [response, error, getProfile]);

  React.useEffect(() => {
    const firstName = _.get(profile, 'first_name');
    if (firstName) {
      dispatch({ type: 'setFirstName', payload: firstName });
      dispatch({ type: 'stepToEmail' });
    }
  }, [profile, dispatch]);

  React.useEffect(() => {
    setSignInError(null);
  }, [values]);

  function submit(e) {
    e.preventDefault();
    signIn(values);
  }

  return (
    <Article name="form" short>
      <Title>
        {'Sign in for free to access your '}
        <em>{ebookName}</em>
        {' ebook'}
      </Title>
      <Desc>
        {'Don’t have a Livewire account? '}
        <a href="#" onClick={switchToSignUp}>Register for free</a>
        {' to access your eBook'}
      </Desc>
      <ReflowContainer short>
        <InputContainer>
          <Input
            type="text"
            placeholder="email"
            {...useInput('email', 'isRequired, isEmail')}
          />
          {errors.email && (
          <HelpBlock>
            {{
              isRequired: 'Email address is required',
              isEmail: 'Email address is invalid',
            }[errors.email]}
          </HelpBlock>
          )}
        </InputContainer>
        <InputContainer>
          <Input
            type="password"
            placeholder="password"
            {...useInput('password', 'isRequired')}
          />
          {errors.password && <HelpBlock>Password is required</HelpBlock>}
        </InputContainer>
        {signInError && (
          <HelpBlock>
            {renderSignInError(signInError)}
          </HelpBlock>
        )}
      </ReflowContainer>
      <Loading variant="button" ref={loadingRef}>
        <Button onClick={submit} disabled={!isValid || signInError}>access my ebook</Button>
      </Loading>
    </Article>
  );
}

SignInForm.propTypes = {
  ebookName: PropTypes.string.isRequired,
};
